/**
 * scripts-components-predictive-search.js
 * -----------------------------------------------------------------------
 * `<predictive-search>`: wraps a real `<form action="/search">` (works
 * with zero JS) and, when enabled, fetches
 * `sections/predictive-search.liquid` through the Predictive Search +
 * Section Rendering API (`routes.predictive_search_url?...&section_id=
 * predictive-search`) as the customer types, swapping the returned HTML
 * into the results listbox. Follows the W3C combobox/listbox pattern:
 * ArrowUp/ArrowDown move `aria-activedescendant` between options, Enter
 * activates the current option, Escape is left to the parent
 * `<am-drawer>`'s native dialog `cancel` handling (closes the drawer).
 *
 * Also owns the lightweight recent-searches feature (localStorage only,
 * no backend) since it needs to hook the same form's submit event.
 * -----------------------------------------------------------------------
 */
(function () {
  'use strict';

  var dom = window.AniMarket && window.AniMarket.dom;
  var debounce = (dom && dom.debounce) || function (fn) { return fn; };

  var RECENT_SEARCHES_KEY = 'am:recent-searches';
  var RECENT_SEARCHES_MAX = 5;
  var DEBOUNCE_MS = 200;
  var MIN_QUERY_LENGTH = 2;

  /* ------------------------------------------------------------------
   * Recent searches (localStorage only — no backend, no PII beyond the
   * search term the customer already typed)
   * ---------------------------------------------------------------- */
  function readRecentSearches() {
    try {
      var raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  }

  function writeRecentSearches(terms) {
    try {
      window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(terms));
    } catch (error) {
      /* localStorage unavailable (private mode, etc.) — recent searches just won't persist */
    }
  }

  function addRecentSearch(term) {
    term = (term || '').trim();
    if (!term) return;
    var terms = readRecentSearches().filter(function (existing) {
      return existing.toLowerCase() !== term.toLowerCase();
    });
    terms.unshift(term);
    writeRecentSearches(terms.slice(0, RECENT_SEARCHES_MAX));
  }

  function removeRecentSearch(term) {
    var terms = readRecentSearches().filter(function (existing) {
      return existing !== term;
    });
    writeRecentSearches(terms);
  }

  class PredictiveSearchElement extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.resultsContainer = this.querySelector('[data-predictive-search-results]');
      this.initialContainer = this.querySelector('[data-predictive-search-initial]');
      this.skeletonTemplate = this.querySelector('[data-predictive-search-skeleton-template]');
      this.recentSection = this.querySelector('[data-search-recent-section]');
      this.recentList = this.querySelector('[data-search-recent]');
      this.recentClearButton = this.querySelector('[data-search-recent-clear]');
      this.form = this.querySelector('form');
      if (!this.input || !this.form) return;

      this.enabled = this.hasAttribute('data-predictive-search-enabled');
      this.endpoint = this.getAttribute('data-predictive-search-url');
      this.limit = this.getAttribute('data-predictive-search-limit') || 4;
      this.types = this.getAttribute('data-predictive-search-types') || 'product';
      this.abortController = null;
      this.activeIndex = -1;
      this.options = [];

      this._onInput = debounce(this._onInput.bind(this), DEBOUNCE_MS);
      this._onKeydown = this._onKeydown.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onClear = this._onClear.bind(this);
      this._onDrawerOpen = this._onDrawerOpen.bind(this);
      this._onRecentClick = this._onRecentClick.bind(this);
      this._onRecentClear = this._onRecentClear.bind(this);

      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('keydown', this._onKeydown);
      this.form.addEventListener('submit', this._onSubmit);
      if (this.clearButton) this.clearButton.addEventListener('click', this._onClear);
      if (this.recentList) this.recentList.addEventListener('click', this._onRecentClick);
      if (this.recentClearButton) this.recentClearButton.addEventListener('click', this._onRecentClear);

      document.addEventListener('am:drawer:open', this._onDrawerOpen);

      this._renderRecentSearches();
      this._toggleClearButton();
    }

    disconnectedCallback() {
      document.removeEventListener('am:drawer:open', this._onDrawerOpen);
    }

    _onDrawerOpen(event) {
      if (!this.closest('am-drawer') || !event.target.contains(this)) return;
      this._renderRecentSearches();
      this.input.focus();
    }

    _toggleClearButton() {
      if (!this.clearButton) return;
      this.clearButton.hidden = this.input.value.trim().length === 0;
    }

    _onClear() {
      this.input.value = '';
      this.input.focus();
      this._toggleClearButton();
      this._showInitialState();
    }

    _onSubmit() {
      addRecentSearch(this.input.value);
    }

    _onRecentClick(event) {
      var removeButton = event.target.closest('[data-search-recent-remove]');
      if (!removeButton) return;
      event.preventDefault();
      removeRecentSearch(removeButton.getAttribute('data-search-recent-remove'));
      this._renderRecentSearches();
    }

    _onRecentClear() {
      writeRecentSearches([]);
      this._renderRecentSearches();
    }

    _renderRecentSearches() {
      if (!this.recentList || !this.recentSection) return;
      var terms = readRecentSearches();
      this.recentSection.hidden = terms.length === 0;
      this.recentList.innerHTML = '';
      terms.forEach(function (term) {
        var item = document.createElement('li');
        item.innerHTML =
          '<span class="search-drawer__chip">' +
          '<a href="' + this.form.action + '?q=' + encodeURIComponent(term) + '">' + this._escape(term) + '</a>' +
          '<button type="button" class="search-drawer__chip-remove" data-search-recent-remove="' + this._escape(term) + '" aria-label="' +
          this._escape(this.getAttribute('data-remove-label') || 'Remove') + ': ' + this._escape(term) + '">&times;</button>' +
          '</span>';
        this.recentList.appendChild(item.firstChild);
      }, this);
    }

    _escape(value) {
      var div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }

    _showInitialState() {
      if (this.resultsContainer) {
        this.resultsContainer.hidden = true;
        this.resultsContainer.innerHTML = '';
      }
      if (this.initialContainer) this.initialContainer.hidden = false;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.options = [];
    }

    _showLoadingState() {
      if (!this.resultsContainer) return;
      this.resultsContainer.hidden = false;
      if (this.initialContainer) this.initialContainer.hidden = true;
      if (this.skeletonTemplate) {
        this.resultsContainer.innerHTML = '';
        this.resultsContainer.appendChild(this.skeletonTemplate.content.cloneNode(true));
      }
      this.input.setAttribute('aria-expanded', 'true');
    }

    _onInput() {
      this._toggleClearButton();
      var term = this.input.value.trim();

      if (!this.enabled || term.length < MIN_QUERY_LENGTH) {
        this._showInitialState();
        return;
      }

      this._showLoadingState();
      this._fetchResults(term);
    }

    _fetchResults(term) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      var url =
        this.endpoint +
        '?q=' + encodeURIComponent(term) +
        '&section_id=predictive-search' +
        '&resources[type]=' + this.types +
        '&resources[limit]=' + this.limit +
        '&resources[options][unavailable_products]=last';

      fetch(url, { signal: this.abortController.signal })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(
          function (text) {
            var parsed = new DOMParser().parseFromString(text, 'text/html');
            var fragment = parsed.querySelector('#shopify-section-predictive-search');
            if (!this.resultsContainer) return;
            this.resultsContainer.innerHTML = fragment ? fragment.innerHTML : '';
            this._indexOptions();
          }.bind(this)
        )
        .catch(function (error) {
          if (error.name === 'AbortError') return;
        });
    }

    _indexOptions() {
      this.options = Array.prototype.slice.call(
        this.resultsContainer.querySelectorAll('[data-predictive-search-option]')
      );
      this.options.forEach(function (option, index) {
        option.id = option.id || 'PredictiveSearchOption-' + index;
      });
      this.activeIndex = -1;
    }

    _setActiveOption(index) {
      if (!this.options.length) return;
      if (this.activeIndex > -1 && this.options[this.activeIndex]) {
        this.options[this.activeIndex].classList.remove('is-active');
      }
      this.activeIndex = (index + this.options.length) % this.options.length;
      var active = this.options[this.activeIndex];
      active.classList.add('is-active');
      this.input.setAttribute('aria-activedescendant', active.id);
      if (typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    _onKeydown(event) {
      if (!this.options.length || this.resultsContainer.hidden) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this._setActiveOption(this.activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this._setActiveOption(this.activeIndex - 1);
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        var active = this.options[this.activeIndex];
        var link = active.tagName === 'A' ? active : active.querySelector('a');
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearchElement);
  }
})();
